import { useJobStyleDataQuery } from "@/hooks/queries/useJobStyleDataQuery";
import { ScheduledJob, SkuData } from "@/types/common.types";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerTable } from "../../common/Table";
import Text from "../../common/Text";
import { getSkuData } from "../utils";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { VirtualTable } from "@/components/common/VirtualTable";
import { PAGE_SIZE } from "@/lib/constants";

const JOB_SKU_DATA_TABLE_COLUMNS_LABELS = {
  styleId: "Style ID",
  image1: "Image 1",
  skuId: "SKU ID",
  articleType: "Article Type",
  averageRating: "Avg. Rating",
  baseColor: "Color",
  brand: "Brand",
  collar: "Collar",
  countryOfOrigin: "Origin",
  discountedPrice: "Discounted Price",
  fabric: "Fabric",
  fabric2: "Fabric 2",
  fit: "Fit",
  gender: "Gender",
  isFastFashion: "Fast Fashion",
  length: "Length",
  mainTrend: "Main Trend",
  manufacturerInfo: "Manufacturer Info",
  masterCategory: "Master Category",
  measurementName1: "Measurement 1",
  measurementValue1: "Measurement Value 1",
  measurementUnit1: "Measurement Unit 1",
  measurementName2: "Measurement 2",
  measurementValue2: "Measurement Value 2",
  measurementUnit2: "Measurement Unit 2",
  mrp: "MRP",
  occasion: "Occasion",
  packerInfo: "Packer Info",
  season: "Season",
  sellerPartnerId: "Seller ID",
  size: "Size",
  sleeveLength: "Sleeve Length",
  subCategory: "Sub Category",
  year: "Year",
};

type JobsDataTableProps = {
  job: ScheduledJob;
  onSkuClick: (sku: SkuData) => void;
  search: string;
  setSearch: (search: string) => void;
  debouncedSearchValue: string;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  setColumnOptions: (options: Array<{ id: string; label: string }>) => void;
  setInitialSelectedColumns: (columns: string[]) => void;
  isDownloading: boolean;
  onDownload: () => void;
};

const JobSKUDataTable: React.FC<JobsDataTableProps> = ({
  job,
  onSkuClick,
  search,
  setSearch,
  debouncedSearchValue,
  columnVisibility,
  setColumnOptions,
  setInitialSelectedColumns,
}) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
  } = useJobStyleDataQuery(job.id.toString(), PAGE_SIZE, debouncedSearchValue);

  const { sendEvent } = useAnalyticsManager();
  useEffect(() => {
    if (debouncedSearchValue) {
      sendEvent("style_sku_search_initiated", {
        search_query: debouncedSearchValue,
      });
    }
  }, [debouncedSearchValue]);

  const [sorting, setSorting] = useState<SortingState>([]);

  const jobDataColumns: ColumnDef<SkuData>[] = useMemo(
    () => [
      {
        accessorKey: "styleId",
        header: "Style ID",
        cell: ({ row }) => (
          <div
            className="cursor-pointer capitalize text-company-purple hover:underline"
            onClick={() => onSkuClick(row.original)}
          >
            {row.original.styleId}
          </div>
        ),
      },
      {
        accessorKey: "image1",
        header: "Image 1",
        cell: ({ row }) => (
          <img
            src={row.original.image1}
            alt="Product"
            className="h-14 w-16 rounded-none object-cover"
          />
        ),
      },
      {
        accessorKey: "skuId",
        header: "SKU ID",
        cell: ({ row }) => <div>{row.original.skuId}</div>,
      },
      {
        accessorKey: "articleType",
        header: "Article Type",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.articleType}</div>
        ),
      },
      {
        accessorKey: "availableCount",
        header: "Available Count",
        cell: ({ row }) => <div>{row.original.availableCount}</div>,
      },
      {
        accessorKey: "averageRating",
        header: "Avg. Rating",
        cell: ({ row }) => (
          <div>{row.original?.averageRating?.toFixed(1) || "N/A"}</div>
        ),
      },
      {
        accessorKey: "baseColor",
        header: "Color",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.baseColor}</div>
        ),
      },
      {
        accessorKey: "brand",
        header: "Brand",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.brand}</div>
        ),
      },
      {
        accessorKey: "collar",
        header: "Collar",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.collar || "N/A"}</div>
        ),
      },
      {
        accessorKey: "countryOfOrigin",
        header: "Origin",
        cell: ({ row }) => <div>{row.original.countryOfOrigin}</div>,
      },
      {
        accessorKey: "discountedPrice",
        header: "Discounted Price",
        cell: ({ row }) => (
          <div>₹{row.original.discountedPrice?.toFixed(2) || "N/A"}</div>
        ),
      },
      {
        accessorKey: "fabric",
        header: "Fabric",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.fabric}</div>
        ),
      },
      {
        accessorKey: "fabric2",
        header: "Fabric 2",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.fabric2}</div>
        ),
      },
      {
        accessorKey: "fit",
        header: "Fit",
        cell: ({ row }) => <div className="capitalize">{row.original.fit}</div>,
      },
      {
        accessorKey: "gender",
        header: "Gender",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.gender}</div>
        ),
      },

      {
        accessorKey: "isFastFashion",
        header: "Fast Fashion",
        cell: ({ row }) => (
          <div>{row.original.isFastFashion ? "Yes" : "No"}</div>
        ),
      },
      {
        accessorKey: "length",
        header: "Length",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.length}</div>
        ),
      },
      {
        accessorKey: "mainTrend",
        header: "Main Trend",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.mainTrend}</div>
        ),
      },
      {
        accessorKey: "manufacturerInfo",
        header: "Manufacturer Info",
        cell: ({ row }) => {
          const fullText = row.original.manufacturerInfo;
          const truncatedText =
            fullText?.split(" ").slice(0, 4).join(" ") +
            (fullText?.split(" ").length > 4 ? "..." : "");

          return (
            <div className="group relative">
              <div className="group-hover:hidden" title={fullText}>
                {truncatedText}
              </div>
              <div className="absolute z-10 hidden min-w-[200px] max-w-[400px] whitespace-normal rounded-none bg-white p-2 shadow-md group-hover:block">
                {fullText}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "masterCategory",
        header: "Master Category",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.masterCategory}</div>
        ),
      },
      {
        accessorKey: "measurementName1",
        header: "Measurement 1",
        cell: ({ row }) => <div>{row.original.measurementName1}</div>,
      },
      {
        accessorKey: "measurementValue1",
        header: "MeasurementValue 1",
        cell: ({ row }) => <div>{row.original.measurementValue1}</div>,
      },
      {
        accessorKey: "measurementUnit1",
        header: "Measurement Unit 1",
        cell: ({ row }) => <div>{row.original.measurementUnit1}</div>,
      },
      {
        accessorKey: "measurementName2",
        header: "Measurement 2",
        cell: ({ row }) => <div>{row.original.measurementName2}</div>,
      },
      {
        accessorKey: "measurementValue2",
        header: "Measurement Value 2",
        cell: ({ row }) => <div>{row.original.measurementValue2}</div>,
      },
      {
        accessorKey: "measurementUnit2",
        header: "Measurement Unit 2",
        cell: ({ row }) => <div>{row.original.measurementUnit2}</div>,
      },

      {
        accessorKey: "mrp",
        header: "MRP",
        cell: ({ row }) => <div>₹{row.original.mrp?.toFixed(2) || "N/A"}</div>,
      },
      {
        accessorKey: "occasion",
        header: "Occasion",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.occasion}</div>
        ),
      },
      {
        accessorKey: "packerInfo",
        header: "Packer Info",
        cell: ({ row }) => {
          const fullText = row.original.manufacturerInfo;
          const truncatedText =
            fullText?.split(" ").slice(0, 4).join(" ") +
            (fullText?.split(" ").length > 4 ? "..." : "");

          return (
            <div className="group relative">
              <div className="group-hover:hidden" title={fullText}>
                {truncatedText}
              </div>
              <div className="absolute z-10 hidden min-w-[200px] max-w-[400px] whitespace-normal rounded-none bg-white p-2 shadow-md group-hover:block">
                {fullText}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "season",
        header: "Season",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.season}</div>
        ),
      },
      {
        accessorKey: "sellerPartnerId",
        header: "Seller ID",
        cell: ({ row }) => <div>{row.original.sellerPartnerId}</div>,
      },
      {
        accessorKey: "size",
        header: "Size",
        cell: ({ row }) => <div className="uppercase">{row.original.size}</div>,
      },
      {
        accessorKey: "sleeveLength",
        header: "Sleeve Length",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.sleeveLength || "N/A"}</div>
        ),
      },
      {
        accessorKey: "subCategory",
        header: "Sub Category",
        cell: ({ row }) => (
          <div className="capitalize">{row.original.subCategory}</div>
        ),
      },
      {
        accessorKey: "year",
        header: "Year",
        cell: ({ row }) => <div>{row.original.year}</div>,
      },
    ],
    [onSkuClick]
  );

  const skuData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => getSkuData(page));
  }, [data?.pages]);

  const table = useReactTable({
    data: skuData,
    columns: jobDataColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
  });

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetching]);

  useEffect(() => {
    if (table) {
      const options = table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => ({
          id: column.id,
          label:
            JOB_SKU_DATA_TABLE_COLUMNS_LABELS[
              column.id as keyof typeof JOB_SKU_DATA_TABLE_COLUMNS_LABELS
            ] || column.id,
        }));
      setColumnOptions(options);

      const selected = table
        .getAllColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setInitialSelectedColumns(selected);
    }
  }, [table, setColumnOptions, setInitialSelectedColumns]);

  if (isLoading) {
    return (
      <JobDataHeader search={search} setSearch={setSearch} totalJobs={0}>
        <ShimmerTable<SkuData> columns={jobDataColumns} rowCount={10} />
      </JobDataHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <JobDataHeader
        search={search}
        setSearch={setSearch}
        totalJobs={data?.pages[0]?.totalElements || 0}
        table={table}
      >
        <VirtualTable
          table={table}
          loadMore={loadMore}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetching}
          columns={jobDataColumns}
        />
      </JobDataHeader>
    </>
  );
};

export default JobSKUDataTable;

type JobDataHeaderProps = {
  search: string;
  setSearch: (search: string) => void;
  totalJobs?: number;
  children: React.ReactNode;
  table?: ReturnType<typeof useReactTable<SkuData>>;
};

const JobDataHeader: React.FC<JobDataHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      {totalJobs && totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
